import { supabase } from "@/integrations/supabase/client";
import { uploadFile } from "@/services/fileStorageService";

export interface InsightResource {
  id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  file_path: string;
  resource_type: string;
  is_gated: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateInsightResourceParams {
  title: string;
  description?: string;
  image_url?: string;
  file_path: string;
  resource_type: string;
  is_gated: boolean;
}

/**
 * Fetches all available insight resources
 */
export async function getInsightResources(): Promise<InsightResource[]> {
  const { data, error } = await supabase
    .from("insight_resources")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching insight resources:", error);
    throw error;
  }

  return data || [];
}

/**
 * Fetches a single insight resource by ID
 */
export async function getInsightResource(id: string): Promise<InsightResource | null> {
  const { data, error } = await supabase
    .from("insight_resources")
    .select("*")
    .eq("id", id)
    .single();

  if (error) {
    console.error(`Error fetching insight resource ${id}:`, error);
    throw error;
  }

  return data;
}

/**
 * Creates a new insight resource
 */
export async function createInsightResource(resource: CreateInsightResourceParams): Promise<InsightResource> {
  const { data, error } = await supabase
    .from("insight_resources")
    .insert(resource)
    .select()
    .single();

  if (error) {
    console.error("Error creating insight resource:", error);
    throw error;
  }

  return data;
}

/**
 * Updates an existing insight resource
 */
export async function updateInsightResource(id: string, updates: Partial<CreateInsightResourceParams>): Promise<InsightResource> {
  const { data, error } = await supabase
    .from("insight_resources")
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error(`Error updating insight resource ${id}:`, error);
    throw error;
  }

  return data;
}

/**
 * Deletes an insight resource
 */
export async function deleteInsightResource(id: string): Promise<void> {
  const { error } = await supabase
    .from("insight_resources")
    .delete()
    .eq("id", id);

  if (error) {
    console.error(`Error deleting insight resource ${id}:`, error);
    throw error;
  }
}

/**
 * Uploads a file for an insight resource and returns the file path
 */
export async function uploadResourceFile(file: File, resourceType: string): Promise<string> {
  const folderPath = `insight-resources/${resourceType}`;
  return await uploadFile("resources", folderPath, file);
}

/**
 * Records a download of a resource by a user
 */
export async function recordResourceDownload(
  firstName: string,
  lastName: string,
  email: string,
  company: string,
  title: string,
  resourceName: string
): Promise<void> {
  const { error } = await supabase.from("contacts").insert({
    first_name: firstName,
    last_name: lastName,
    email,
    company,
    title,
    resource_downloaded: resourceName,
    lead_list_name: "resource_download",
    sent_to_apollo: false,
    submitted_at: new Date().toISOString(),
  });

  if (error) {
    console.error("Error recording resource download:", error);
    throw error;
  }
}
